import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  Button,
  Checkbox,
  Label,
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@design-system';
import { useSendPuiDialog } from '../hooks/use-send-pui-dialog';
import { PuiLoadingSequence } from './pui-loading-sequence';
import { ArrowBack } from '@mui/icons-material';

interface SendPuiDialogProps {
  open: boolean;
  onClose: () => void;
  onSend: (options: SendAnonymousPuiOptions) => void;
  isPending: boolean;
  isSuccess: boolean;
  availablePolicies: PolicyInfo[];
  sentPuiData?: PUIMetadata[];
}

export function SendPuiDialog({
  open,
  onClose,
  onSend,
  isPending,
  isSuccess,
  availablePolicies,
  sentPuiData = [],
}: SendPuiDialogProps) {
  const {
    step,
    selectedPolicies,
    showLoadingSequence,
    handleTogglePolicy,
    handleContinue,
    handleBack,
    handleSend,
    handleLoadingComplete,
  } = useSendPuiDialog({
    open,
    onSend,
    onClose,
    availablePolicies,
    isPending,
    isSuccess,
  });

  const selectPoliciesContent = () => {
    return (
      <>
        <DialogHeader>
          <DialogTitle>Select Policies</DialogTitle>
          <DialogDescription>
            Choose which policies to include in the anonymous PUI request
          </DialogDescription>
        </DialogHeader>

        <div className="mt-4 space-y-3">
          {availablePolicies.length === 0 ? (
            <p className="text-muted-foreground text-sm">No policies available</p>
          ) : (
            <TooltipProvider>
              {availablePolicies.map(policy => {
                const hasBeenSent = sentPuiData.some(
                  pui => pui.productName === policy.productName && pui.isJoint === policy.isJoint,
                );

                const baseName = policy.isJoint
                  ? `${policy.productName} (Joint)`
                  : policy.productName;

                const checkboxContent = (
                  <div className="flex items-center space-x-2">
                    <Checkbox
                      id={policy.uniqueKey}
                      checked={selectedPolicies.has(policy.uniqueKey)}
                      onCheckedChange={() => handleTogglePolicy(policy.uniqueKey)}
                      disabled={!policy.supportsPUI}
                    />
                    <Label
                      htmlFor={policy.uniqueKey}
                      className={`text-sm font-normal ${
                        policy.supportsPUI ? 'cursor-pointer' : 'cursor-not-allowed opacity-50'
                      }`}
                    >
                      {baseName}
                      {hasBeenSent && (
                        <span className="ml-1 font-medium text-yellow-600">Send Again?</span>
                      )}
                    </Label>
                  </div>
                );

                if (!policy.supportsPUI) {
                  return (
                    <Tooltip key={policy.uniqueKey}>
                      <TooltipTrigger asChild>{checkboxContent}</TooltipTrigger>
                      <TooltipContent>
                        <p className="text-xs">This carrier does not support anonymized PUIs</p>
                      </TooltipContent>
                    </Tooltip>
                  );
                }

                return <div key={policy.uniqueKey}>{checkboxContent}</div>;
              })}
            </TooltipProvider>
          )}
        </div>

        <div className="mt-6 flex justify-between">
          <Button
            onClick={onClose}
            variant="outline"
          >
            Cancel
          </Button>
          <Button
            onClick={handleContinue}
            disabled={selectedPolicies.size === 0}
          >
            Continue
          </Button>
        </div>
      </>
    );
  };

  const selectDeliveryContent = () => {
    return (
      <>
        <DialogHeader>
          <DialogTitle>Send Anonymous PUI</DialogTitle>
          <DialogDescription>
            Choose how you would like to send the preliminary underwriting inquiry
          </DialogDescription>
        </DialogHeader>

        <div className="mt-4 flex flex-col gap-3">
          <div className="flex gap-3">
            <Button
              onClick={() => handleSend()}
              disabled={isPending}
              variant="default"
              className="flex-1"
            >
              Send to Carrier{selectedPolicies.size > 1 ? 's' : ''}
            </Button>

            <Button
              onClick={() => handleSend(true)}
              disabled={isPending}
              variant="outline"
              className="flex-1"
            >
              Send Test Email to Self
            </Button>
          </div>
        </div>

        <div className="mt-4">
          <Button
            onClick={handleBack}
            variant="ghost"
            size="sm"
          >
            <ArrowBack /> Back to Policy Selection
          </Button>
        </div>
      </>
    );
  };

  return (
    <Dialog
      open={open}
      onOpenChange={onClose}
    >
      <DialogContent>
        {showLoadingSequence && isPending ? (
          <PuiLoadingSequence
            isDataLoaded={isSuccess}
            onComplete={handleLoadingComplete}
          />
        ) : step === 'select-policies' ? (
          selectPoliciesContent()
        ) : (
          selectDeliveryContent()
        )}
      </DialogContent>
    </Dialog>
  );
}
